
import { ref } from "vue";
import {
  IonContent,
  IonHeader,
  IonButtons,
  IonBackButton,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonSpinner,
  IonTitle,
  IonText,
  IonToolbar,
  IonRow,
  IonCol,
  onIonViewDidEnter,
} from "@ionic/vue";
import { chevronBack, alertCircleOutline } from "ionicons/icons";
import EmptyView from "@/components/EmptyView.vue";
import { ViewState } from "@/enums/ViewState";
import { CoinModel } from "@/models/CoinModel";
import CoinService from "@/services/CoinService";
import { useRoute } from "vue-router";
import {
  CategoryScale,
  Chart,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import { useIntl } from "vue-intl";

export default {
  name: "CoinDetail",
  components: {
    IonPage,
    IonContent,
    IonHeader,
    IonButtons,
    IonBackButton,
    IonRefresher,
    IonRefresherContent,
    IonTitle,
    IonToolbar,
    IonRow,
    IonCol,
    IonText,
    IonSpinner,
    EmptyView,
  },
  setup() {
    const ionRefresher = ref();
    const canvasRef = ref();

    const viewState = ref<ViewState>(ViewState.INITIAL);
    const coin = ref<CoinModel>();
    const route = useRoute();
    const intl = useIntl();

    const onRefreshComplete = () => {
      ionRefresher.value.$el.complete();
    };

    const buildChart = (chartData: any) => {
      Chart.register(
        CategoryScale,
        LinearScale,
        LineController,
        PointElement,
        LineElement,
        Tooltip
      );

      const context = canvasRef.value.getContext("2d");

      const labels = chartData.prices.map((a: number[]) =>
        intl.formatDate(a[0], {
          dateStyle: "medium",
        })
      );
      const coinHistory = chartData.prices.map((a: number[]) => a[1]);

      return new Chart(context, {
        type: "line",
        data: {
          labels: labels,
          datasets: [
            {
              data: coinHistory,
              borderColor: "#f8f8f8",
              fill: false,
            },
          ],
        },
        options: {
          plugins: {
            tooltip: {
              callbacks: {
                label: (tooltipItems: any) => {
                  const price = tooltipItems.raw as number;
                  return intl.formatNumber(price, {
                    style: "currency",
                    currency: "USD",
                    maximumFractionDigits: 8,
                  });
                },
              },
            },
          },
          responsive: true,
          scales: {
            xAxes: {
              display: false,
            },
            yAxes: {
              display: false,
            },
          },
        },
      });
    };

    const loadCoin = async () => {
      viewState.value = ViewState.LOADING;

      try {
        const id = route.params.id as string;
        const [chartResponse, coinResponse] = await Promise.all([
          CoinService.getChart(id),
          CoinService.loadOne(id),
        ]);

        const coinData = coinResponse.data[id];
        coinData.name = id;
        coin.value = coinData;

        if (coin.value) {
          viewState.value = ViewState.CONTENT;
          setTimeout(() => buildChart(chartResponse.data), 300);
        } else {
          viewState.value = ViewState.EMPTY;
        }

      } catch {
        viewState.value = ViewState.ERROR;
      } finally {
        onRefreshComplete();
      }
    };

    const onReload = () => {
      loadCoin();
    };

    onIonViewDidEnter(() => {
      if (viewState.value !== ViewState.CONTENT) {
        loadCoin();
      }
    });

    return {
      viewState,
      ViewState,
      onReload,
      coin,
      ionRefresher,
      canvasRef,
      chevronBack,
      alertCircleOutline,
    };
  },
};
